import React from 'react';
import { useUIStore } from '@/store/uiStore';
import { motion, AnimatePresence } from 'framer-motion';
import { getText } from '../../translate/text';

const LoadingOverlay = ({ fullscreen = true }) => {
  const { isLoading } = useUIStore();

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`
                        ${fullscreen ? 'fixed inset-0' : 'absolute inset-0'}
                        z-50
                        flex
                        items-center
                        justify-center
                        backdrop-blur-sm
                        bg-white/30
                        dark:bg-gray-900/30
                    `}
        >
          <div className="relative">
            {/* Spinner image */}
            <motion.img
              src="https://static.wixstatic.com/media/fc5088_5dba11dfa34b41f889449b75011175e6~mv2.png/v1/fill/w_142,h_142,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/logo_dark_background.png"
              alt="Loading..."
              animate={{
                rotate: -360,
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear',
              }}
              className="w-16 h-16"
            />

            {/* Secondary spinner (opposite direction) */}
            <motion.div
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'linear',
              }}
              className="
                        absolute
                        inset-0
                        w-16
                        h-16
                        border-4
                        border-transparent
                        border-b-primary-600
                        dark:border-b-primary-400
                        rounded-full
                      "
            />

            {/* Loading text */}
            <div
              className="
                        absolute
                        top-full
                        left-1/2
                        -translate-x-1/2
                        mt-4
                        text-center
                        w-32
                    "
            >
              <p
                className="
                          text-sm
                          font-medium
                          text-gray-900
                          dark:text-white
                      "
              >
                {getText('loading')}
              </p>
              <p
                className="
                          text-xs
                          text-gray-500
                          dark:text-gray-400
                          mt-1
                      "
              >
                {getText('please_wait')}
              </p>
            </div>

            {/* Optional progress indicator */}
            {useUIStore.getState().loadingProgress > 0 && (
              <div
                className="
                                absolute
                                bottom-0
                                left-1/2
                                -translate-x-1/2
                                mb-4
                                w-32
                            "
              >
                <div
                  className="
                                    h-1
                                    bg-gray-200
                                    dark:bg-gray-700
                                    rounded-full
                                    overflow-hidden
                                "
                >
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{
                      width: `${useUIStore.getState().loadingProgress}%`,
                    }}
                    transition={{ duration: 0.3 }}
                    className="
                                            h-full
                                            bg-primary-600
                                            dark:bg-primary-400
                                        "
                  />
                </div>
                <p
                  className="
                                    text-xs
                                    text-center
                                    text-gray-500
                                    dark:text-gray-400
                                    mt-1
                                "
                >
                  {useUIStore.getState().loadingProgress}%
                </p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Variants for spinner animations
const spinnerVariants = {
  rotate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: 'linear',
    },
  },
};

// Mini loading spinner component for inline use
export const LoadingSpinner = ({ size = 'md', color = 'primary' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
    lg: 'w-8 h-8 border-3',
    xl: 'w-12 h-12 border-4',
  };

  const colorClasses = {
    primary:
      'border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400',
    secondary: 'border-gray-200 border-t-gray-600 dark:border-gray-800 dark:border-t-gray-400',
    white: 'border-white/30 border-t-white',
  };

  return (
    <motion.div
      variants={spinnerVariants}
      animate="rotate"
      className={`
                ${sizeClasses[size]}
                ${colorClasses[color]}
                rounded-full
                inline-block
            `}
    />
  );
};

// Loading button component
export const LoadingButton = ({
  loading,
  children,
  disabled,
  className = '',
  spinnerSize = 'sm',
  ...props
}) => {
  return (
    <button
      disabled={loading || disabled}
      className={`
                relative
                inline-flex
                items-center
                justify-center
                ${loading ? 'cursor-wait' : ''}
                ${className}
            `}
      {...props}
    >
      {loading && (
        <span className="absolute left-1/2 -translate-x-1/2">
          <LoadingSpinner size={spinnerSize} color="white" />
        </span>
      )}
      <span className={loading ? 'invisible' : ''}>{children}</span>
    </button>
  );
};

export default LoadingOverlay;
